import React, { useState, useEffect } from 'react';
import atomicService from '../../services/atomicService';
import mitreService from '../../services/mitreService';
import { useTheme } from '../../contexts/ThemeContext';

const PurpleHQTestManager = () => {
  const { darkMode } = useTheme();
  const [customTests, setCustomTests] = useState([]);
  const [mitreTechniques, setMitreTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'clone'
  const [selectedTest, setSelectedTest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    techniqueId: '',
    description: '',
    platforms: [],
    atomicTests: [{
      name: '',
      description: '',
      supportedPlatforms: [],
      executor: {
        type: 'powershell',
        command: '',
        elevationRequired: false
      },
      inputArguments: []
    }]
  });

  const [newArgument, setNewArgument] = useState({
    name: '',
    description: '',
    type: 'string',
    default: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customData, mitreData] = await Promise.all([
        atomicService.getCustomTests(),
        mitreService.getTechniques()
      ]);
      setCustomTests(customData.tests || []);
      setMitreTechniques(Array.isArray(mitreData) ? mitreData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load custom tests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = () => {
    setModalMode('create');
    setFormData({
      name: '',
      techniqueId: '',
      description: '',
      platforms: [],
      atomicTests: [{
        name: '',
        description: '',
        supportedPlatforms: [],
        executor: {
          type: 'powershell',
          command: '',
          elevationRequired: false
        },
        inputArguments: []
      }]
    });
    setShowModal(true);
  };

  const handleEditTest = (test) => {
    setModalMode('edit');
    setSelectedTest(test);
    setFormData({
      name: test.name || '',
      techniqueId: test.techniqueId || '',
      description: test.description || '',
      platforms: test.platforms || [],
      atomicTests: test.atomicTests || [{
        name: '',
        description: '',
        supportedPlatforms: [],
        executor: { type: 'powershell', command: '', elevationRequired: false },
        inputArguments: []
      }]
    });
    setShowModal(true);
  };

  const handleCloneTest = async (testId) => {
    try {
      const response = await atomicService.cloneTest(testId);
      if (response.success) {
        alert('Test cloned successfully! You can now edit it.');
        await fetchData();
      }
    } catch (error) {
      console.error('Error cloning test:', error);
      alert('Failed to clone test');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this custom test?')) {
      return;
    }

    try {
      await atomicService.deleteCustomTest(testId);
      await fetchData();
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete test');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.techniqueId) {
      alert('Name and MITRE Technique ID are required');
      return;
    }

    if (formData.atomicTests.length === 0 || !formData.atomicTests[0].name) {
      alert('At least one atomic test with a name is required');
      return;
    }

    try {
      if (modalMode === 'create') {
        await atomicService.createCustomTest(formData);
      } else if (modalMode === 'edit') {
        await atomicService.updateCustomTest(selectedTest._id, formData);
      }

      await fetchData();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving test:', error);
      alert(`Failed to ${modalMode === 'create' ? 'create' : 'update'} test`);
    }
  };

  const updateAtomicTest = (index, field, value) => {
    const updated = [...formData.atomicTests];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[index][parent][child] = value;
    } else {
      updated[index][field] = value;
    }
    setFormData({ ...formData, atomicTests: updated });
  };

  const addArgument = (testIndex) => {
    if (!newArgument.name) {
      alert('Argument name is required');
      return;
    }

    const updated = [...formData.atomicTests];
    if (!updated[testIndex].inputArguments) {
      updated[testIndex].inputArguments = [];
    }
    updated[testIndex].inputArguments.push({ ...newArgument });
    setFormData({ ...formData, atomicTests: updated });
    setNewArgument({ name: '', description: '', type: 'string', default: '' });
  };

  const removeArgument = (testIndex, argIndex) => {
    const updated = [...formData.atomicTests];
    updated[testIndex].inputArguments.splice(argIndex, 1);
    setFormData({ ...formData, atomicTests: updated });
  };

  const filteredTests = customTests.filter(test => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (test.name || '').toLowerCase().includes(query) ||
      (test.techniqueId || '').toLowerCase().includes(query) ||
      (test.description || '').toLowerCase().includes(query)
    );
  });

  const platformOptions = ['windows', 'linux', 'darwin', 'macos', 'iaas:aws', 'iaas:azure', 'iaas:gcp'];
  const executorTypes = ['powershell', 'cmd', 'bash', 'sh', 'python', 'manual'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            🧪 PurpleHQ Custom Tests
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Create and manage custom security tests for your organization
          </p>
        </div>
        <button
          onClick={handleCreateTest}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
        >
          <span className="text-lg">+</span>
          Create Custom Test
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search custom tests..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
        />
      </div>

      {/* Test List */}
      <div className="space-y-4">
        {filteredTests.length === 0 ? (
          <div className={`text-center py-12 rounded-lg border-2 border-dashed ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            <p className="text-lg mb-2">No custom tests found</p>
            <p className="text-sm">Create your first custom test or clone an existing Atomic Red Team test</p>
          </div>
        ) : (
          filteredTests.map(test => (
            <div
              key={test._id}
              className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🧪</span>
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {test.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                      {test.techniqueId}
                    </span>
                    {test.source === 'custom' && (
                      <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        Custom
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {test.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>
                      Platforms: {(test.platforms || []).join(', ') || 'all'}
                    </span>
                    <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}>
                      {test.atomicTests?.length || 0} test variant(s)
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleEditTest(test)}
                    className={`px-3 py-1 text-sm rounded ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTest(test._id)}
                    className={`px-3 py-1 text-sm rounded ${darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {modalMode === 'create' ? 'Create Custom Test' : 'Edit Custom Test'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className={`text-2xl ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Test Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                    placeholder="e.g., Custom Credential Harvesting Test"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    MITRE Technique ID *
                  </label>
                  <select
                    required
                    value={formData.techniqueId}
                    onChange={(e) => setFormData({ ...formData, techniqueId: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  >
                    <option value="">Select a technique</option>
                    {mitreTechniques.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.id} - {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                    placeholder="Describe what this test does..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Platforms
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {platformOptions.map(platform => (
                      <label key={platform} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.platforms.includes(platform)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, platforms: [...formData.platforms, platform] });
                            } else {
                              setFormData({ ...formData, platforms: formData.platforms.filter(p => p !== platform) });
                            }
                          }}
                          className="rounded"
                        />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {platform}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Atomic Test Details */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Test Implementation
                </h4>

                {formData.atomicTests.map((test, index) => (
                  <div key={index} className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Test Variant Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={test.name}
                        onChange={(e) => updateAtomicTest(index, 'name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        placeholder="e.g., Test via PowerShell"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Test Description
                      </label>
                      <textarea
                        value={test.description}
                        onChange={(e) => updateAtomicTest(index, 'description', e.target.value)}
                        rows={2}
                        className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Executor Type
                        </label>
                        <select
                          value={test.executor.type}
                          onChange={(e) => updateAtomicTest(index, 'executor.type', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        >
                          {executorTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={test.executor.elevationRequired}
                            onChange={(e) => updateAtomicTest(index, 'executor.elevationRequired', e.target.checked)}
                            className="rounded"
                          />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Requires Elevation
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Command *
                      </label>
                      <textarea
                        required
                        value={test.executor.command}
                        onChange={(e) => updateAtomicTest(index, 'executor.command', e.target.value)}
                        rows={6}
                        className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        placeholder="Enter command or script... Use #{variable_name} for input arguments"
                      />
                    </div>

                    {/* Input Arguments */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Input Arguments (Optional)
                      </label>

                      {test.inputArguments && test.inputArguments.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {test.inputArguments.map((arg, argIndex) => (
                            <div key={argIndex} className={`p-3 rounded border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="font-mono text-sm font-medium">{arg.name}</div>
                                  <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {arg.description}
                                  </div>
                                  <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    Type: {arg.type} | Default: {arg.default || '(none)'}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeArgument(index, argIndex)}
                                  className="text-red-500 hover:text-red-600 ml-2"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Argument Form */}
                      <div className={`p-3 rounded border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="name"
                            value={newArgument.name}
                            onChange={(e) => setNewArgument({ ...newArgument, name: e.target.value })}
                            className={`px-2 py-1 text-sm border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          />
                          <input
                            type="text"
                            placeholder="description"
                            value={newArgument.description}
                            onChange={(e) => setNewArgument({ ...newArgument, description: e.target.value })}
                            className={`px-2 py-1 text-sm border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          />
                          <select
                            value={newArgument.type}
                            onChange={(e) => setNewArgument({ ...newArgument, type: e.target.value })}
                            className={`px-2 py-1 text-sm border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          >
                            <option value="string">string</option>
                            <option value="path">path</option>
                            <option value="url">url</option>
                            <option value="integer">integer</option>
                          </select>
                          <input
                            type="text"
                            placeholder="default value"
                            value={newArgument.default}
                            onChange={(e) => setNewArgument({ ...newArgument, default: e.target.value })}
                            className={`px-2 py-1 text-sm border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => addArgument(index)}
                          className={`text-xs px-3 py-1 rounded ${darkMode ? 'bg-green-900 text-green-300 hover:bg-green-800' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        >
                          + Add Argument
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  {modalMode === 'create' ? 'Create Test' : 'Update Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurpleHQTestManager;
